import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../api/client';
import { useAuthStore } from '../../../store/authStore';

/**
 * Comunicado expandido com campos municipais.
 */
export interface ComunicadoMunicipal {
  id: string;
  titulo: string;
  corpo: string;
  tipo: string;
  categoria: string;
  destinatario: string;
  status: string;
  fixado: boolean;
  turmaId?: string;
  escolaId?: string;
  municipioId?: string;
  criadoPorId?: string;
  dataEvento?: string;
  agendadoPara?: string;
  templateId?: string;
  configPreview?: Record<string, boolean>;
  ativo: boolean;
  criadoEm: string;
  atualizadoEm: string;
  escola?: { id: string; nome: string } | null;
  turma?: { id: string; nome: string } | null;
  municipio?: { id: string; nome: string } | null;
  criadoPor?: { id: string; nome: string; perfil: string } | null;
  _count?: { visualizacoes: number };
}

export interface ComunicadoStats {
  total: number;
  enviados: number;
  agendados: number;
  rascunhos: number;
  totalVisualizacoes: number;
  comunicadosVisualizados: number;
  taxaAbertura: number;
  porCategoria: { categoria: string; total: number }[];
  porTipo: { tipo: string; total: number }[];
}

interface Filters {
  categoria?: string;
  status?: string;
  escolaId?: string;
}

/**
 * Hook dedicado para comunicados municipais.
 * Usa /municipios/:municipioId/comunicados (NÃO usa useCrud).
 */
export function useMunicipalComunicados(filters?: Filters) {
  const queryClient = useQueryClient();
  const municipioId = useAuthStore((s) => s.user?.municipioId);

  const basePath = municipioId ? `/municipios/${municipioId}/comunicados` : '';

  // Lista de comunicados
  const query = useQuery({
    queryKey: ['municipal-comunicados', municipioId, filters],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (filters?.categoria) params.categoria = filters.categoria;
      if (filters?.status) params.status = filters.status;
      if (filters?.escolaId) params.escolaId = filters.escolaId;
      const { data } = await api.get(basePath, { params });
      return (Array.isArray(data) ? data : []) as ComunicadoMunicipal[];
    },
    enabled: !!municipioId,
  });

  // Stats/KPIs
  const statsQuery = useQuery({
    queryKey: ['municipal-comunicados-stats', municipioId],
    queryFn: async () => {
      const { data } = await api.get(`${basePath}/stats`);
      return data as ComunicadoStats;
    },
    enabled: !!municipioId,
  });

  // Criar comunicado
  const create = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const { data } = await api.post(basePath, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['municipal-comunicados'] });
      queryClient.invalidateQueries({ queryKey: ['municipal-comunicados-stats'] });
    },
  });

  // Atualizar comunicado
  const update = useMutation({
    mutationFn: async ({ id, ...payload }: { id: string } & Record<string, unknown>) => {
      const body = (payload && typeof (payload as any).data === 'object' && (payload as any).data !== null)
        ? { ...(payload as any).data, ...payload }
        : payload;
      delete (body as any).data;
      const { data } = await api.put(`${basePath}/${id}`, body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['municipal-comunicados'] });
      queryClient.invalidateQueries({ queryKey: ['municipal-comunicados-stats'] });
    },
  });

  // Soft-delete
  const deactivate = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete(`${basePath}/${id}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['municipal-comunicados'] });
      queryClient.invalidateQueries({ queryKey: ['municipal-comunicados-stats'] });
    },
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    stats: statsQuery.data ?? null,
    statsLoading: statsQuery.isLoading,
    create,
    update,
    deactivate,
  };
}
